/* import __COLOCATED_TEMPLATE__ from './priority-changed.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type Session from 'embercom/services/session';
import type RenderablePart from 'embercom/objects/inbox/renderable-part';
import type PriorityChanged from 'embercom/objects/inbox/renderable/priority-changed';
import LinkWithText from 'embercom/helpers/link-with-text';
import { createSafeAdminLink } from 'embercom/utils/admin-link';
import { FEATURE_FLAG_NAME } from 'embercom/lib/inbox2/event-text-truncation';

interface PriorityChangedPart extends RenderablePart {
  renderableData: PriorityChanged;
}

interface Args {
  part: PriorityChangedPart;
  isTicket: boolean;
}

interface Signature {
  Args: Args;
}

export default class Inbox2ConversationStreamEventPartsPriorityChangedComponent extends Component<Signature> {
  @service declare session: Session;

  // @ts-ignore helper doesn't seem to be typed correctly
  linkWithTextHelper = LinkWithText.create(this);

  get renderableData() {
    return this.args.part.renderableData;
  }

  get teammate() {
    return this.renderableData.adminSummary;
  }

  get isConversationEventsUiImprovementsEnabled(): boolean {
    return this.session.workspace?.isFeatureEnabled(FEATURE_FLAG_NAME) ?? false;
  }

  get translationKey(): string {
    let isCurrentUser = this.teammate.id === this.session.teammate.id;
    let isPriority = this.renderableData.isPriority;

    if (this.isConversationEventsUiImprovementsEnabled && isCurrentUser && isPriority) {
      return 'inbox.priority-changed.you-added-priority-v2';
    } else if (this.isConversationEventsUiImprovementsEnabled && isCurrentUser && !isPriority) {
      return 'inbox.priority-changed.you-removed-priority-v2';
    } else if (this.isConversationEventsUiImprovementsEnabled && !isCurrentUser && isPriority) {
      return 'inbox.priority-changed.admin-added-priority-v2';
    } else if (this.isConversationEventsUiImprovementsEnabled && !isCurrentUser && !isPriority) {
      return 'inbox.priority-changed.admin-removed-priority-v2';
    } else if (isCurrentUser && isPriority) {
      return 'inbox.priority-changed.you-added-priority';
    } else if (isCurrentUser && !isPriority) {
      return 'inbox.priority-changed.you-removed-priority';
    } else if (!isCurrentUser && isPriority) {
      return 'inbox.priority-changed.admin-added-priority';
    } else {
      return 'inbox.priority-changed.admin-removed-priority';
    }
  }

  get translationData() {
    let part = this.args.isTicket ? 'ticket' : 'conversation';
    if (this.teammate.id === this.session.teammate.id) {
      return {
        data: { part },
      };
    } else {
      return {
        data: {
          who: createSafeAdminLink(this.teammate, this.linkWithTextHelper),
          part,
        },
      };
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationStream::EventParts::PriorityChanged': typeof Inbox2ConversationStreamEventPartsPriorityChangedComponent;
    'inbox2/conversation-stream/event-parts/priority-changed': typeof Inbox2ConversationStreamEventPartsPriorityChangedComponent;
  }
}
